import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth, dashboardPathForRole } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";

const NAV_LINKS = [
  { to: "/", label: "Beranda" },
  { to: "/tentang-kami", label: "Tentang Kami" },
  { to: "/program", label: "Program" },
  { to: "/pembimbing", label: "Pembimbing" },
  { to: "/manasik", label: "Jadwal" },
  { to: "/galeri", label: "Galeri" },
  { to: "/artikel", label: "Artikel" },
  { to: "/faq", label: "FAQ" },
  { to: "/kontak", label: "Kontak" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const { settings } = useSite();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 w-full border-b border-white/40 bg-white/85 backdrop-blur-xl shadow-sm" data-testid="navbar">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-2.5" data-testid="navbar-logo">
          <img
            src="https://ik.imagekit.io/sekawan/KBIH/logo%20KBIHsby.png"
            alt="KBIHU Muhammadiyah Surabaya"
            className="h-11 w-11 shrink-0 object-contain"
          />
          <div className="leading-tight">
            <span className="block font-heading text-sm font-bold uppercase tracking-tight text-[#1F2937] sm:text-base">KBIHU Muhammadiyah</span>
            <span className="block font-heading text-xs font-semibold uppercase tracking-wide text-[#0F6B4F] sm:text-sm">Surabaya</span>
          </div>
        </Link>

        <nav className="hidden items-center gap-6 lg:flex">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors hover:text-[#0F6B4F] ${isActive ? "text-[#0F6B4F]" : "text-[#1F2937]"}`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          {user ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                data-testid="navbar-dashboard-btn"
                onClick={() => navigate(dashboardPathForRole(user.role))}
                className="gap-2 text-[#0F6B4F] hover:bg-[#0F6B4F]/10"
              >
                <LayoutDashboard className="h-4 w-4" /> Dashboard
              </Button>
              <Button
                variant="outline"
                size="sm"
                data-testid="navbar-logout-btn"
                onClick={() => logout().then(() => navigate("/"))}
                className="gap-2 rounded-full border-[#0F6B4F]/30"
              >
                <LogOut className="h-4 w-4" /> Keluar
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                data-testid="navbar-login-btn"
                onClick={() => navigate("/login")}
                className="text-[#1F2937] hover:bg-[#0F6B4F]/10"
              >
                Masuk
              </Button>
              <Button
                size="sm"
                data-testid="navbar-register-btn"
                onClick={() => navigate("/register")}
                className="rounded-full bg-gradient-to-r from-[#0F6B4F] to-[#1677B8] px-5 text-white hover:opacity-90 transition-opacity"
              >
                Daftar
              </Button>
            </>
          )}
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button className="flex items-center justify-center rounded-lg p-2 text-[#1F2937] lg:hidden" data-testid="navbar-mobile-toggle" aria-label="Menu">
              <Menu className="h-6 w-6" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[85%] bg-white">
            <div className="mt-8 flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `rounded-lg px-4 py-3 text-base font-medium ${isActive ? "bg-[#0F6B4F]/10 text-[#0F6B4F]" : "text-[#1F2937]"}`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <div className="mt-4 flex flex-col gap-2 border-t border-slate-100 pt-4">
                {user ? (
                  <>
                    <Button
                      data-testid="mobile-dashboard-btn"
                      onClick={() => { setOpen(false); navigate(dashboardPathForRole(user.role)); }}
                      className="w-full rounded-full bg-[#0F6B4F] text-white"
                    >
                      Dashboard
                    </Button>
                    <Button
                      variant="outline"
                      data-testid="mobile-logout-btn"
                      onClick={() => { setOpen(false); logout().then(() => navigate("/")); }}
                      className="w-full rounded-full"
                    >
                      Keluar
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      data-testid="mobile-login-btn"
                      onClick={() => { setOpen(false); navigate("/login"); }}
                      className="w-full rounded-full"
                    >
                      Masuk
                    </Button>
                    <Button
                      data-testid="mobile-register-btn"
                      onClick={() => { setOpen(false); navigate("/register"); }}
                      className="w-full rounded-full bg-gradient-to-r from-[#0F6B4F] to-[#1677B8] text-white"
                    >
                      Daftar
                    </Button>
                  </>
                )}
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
